import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useContext,
} from 'react';
import { Input, Tooltip, Button, ConfigProvider } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  ClockCircleOutlined,
  FileSearchOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import Table from '@components/Table';
import useSwr from '@hooks/useSwr';
import moment from 'moment';
import {
  dateFormat,
  formatDateTime,
  toISOStringWithOffset,
} from '@utils/formatters';
import QuickRangePicker from '@components/DatePicker/QuickRangePicker';
import { getAntLocale } from '@utils/locales';
import RequestReport from '@components/RequestReport';
import rolesConstants from '@utils/rolesConstants';
import useSearchParams from '@hooks/useSearchParams';
import './styles.less';
import ActionTypes from '@utils/deviceActions';
import DeviceActionModal from '@components/DeviceActionModal';
import CustomTag, { TagColors } from '@components/CustomTag';
import SessionContext from '@providers/Session/SessionContext';
import SmsSendDetails from '../SmsSendDetails';

const { Search } = Input;

export type SmsAvailableOptions = {
  deliveryTimeWindow: boolean;
  scheduled: boolean;
  previewNormalize: boolean;
  previewLanguageCode: boolean;
  sender: boolean;
  flash: boolean;
  apiUrl: boolean;
  apiKey: boolean;
  apiSecret: boolean;
};

type SendSmsRequest = {
  id: string;
  from: string;
  message: string;
  status: string;
  flash: boolean;
  validityMinutes: number;
  createdBy: string;
  createdAt: string;
  deliveryTimeWindowFrom: string;
  deliveryTimeWindowTo: string;
  deliveryTimeWindowDays: string[];
  sendAt: string;
};

type RecordType = {
  sendSmsRequest?: SendSmsRequest;
  successCount?: number;
  deliveryTime?: string;
  pendingCount: number;
  errorCount: number;
};

const SmsRequest = () => {
  const {
    t: translate,
    i18n: { language },
  } = useTranslation(['sms-send']);
  const { smsAvailableOptions } = useContext(SessionContext);
  const [visible, setVisible] = useState(false);
  const [tableToShow, setTableToShow] = useState('batch');
  const [createdByValue, setCreatedByValue] = useState('');
  const [messageValue, setMessageValue] = useState('');
  const [idValue, setIdValue] = useState('');

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: ['createdBy', 'id', 'message', 'createdAtTo', 'createdAtFrom'],
  });

  const {
    createdBy,
    id,
    message,
    createdAtTo,
    createdAtFrom,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  const filters = useMemo(() => {
    const allFilters = {
      createdBy,
      id,
      message,
      createdAtTo,
      createdAtFrom,
      page: page ? Number(page) - 1 : 0,
      size: linesPerPage ?? 20,
    };

    return allFilters;
  }, [linesPerPage, createdBy, id, message, createdAtTo, createdAtFrom, page]);

  const { data, mutate } = useSwr(`/service-proxy/sms-request`, {
    params: filters,
  });

  const columnListStatus = useCallback(
    (success: string, row: RecordType) => {
      const list = [
        {
          label: 'SUCCESS',
          color: 'green',
          value: success,
        },
        {
          label: 'PROCESSING',
          color: 'blue',
          value: row.pendingCount,
        },
        {
          label: 'ERROR',
          color: 'red',
          value: row.errorCount,
        },
      ];

      return (
        <>
          {list.map(({ label, color, value }) => (
            <Tooltip key={label} title={translate(`status.${label}`)}>
              <CustomTag
                icon={<ClockCircleOutlined />}
                color={color as TagColors}
                style={{ cursor: 'pointer' }}
              >
                {value ?? 0}
              </CustomTag>
            </Tooltip>
          ))}
        </>
      );
    },
    [translate],
  );

  const messageColumn = useCallback((messageToFormat: string) => {
    return messageToFormat.length > 20 ? (
      <Tooltip title={messageToFormat}>
        {`${messageToFormat.substring(0, 20)}...`}
      </Tooltip>
    ) : (
      messageToFormat
    );
  }, []);
  const columnDeliveryTime = useCallback(
    (record: RecordType) => {
      if (
        record?.sendSmsRequest?.deliveryTimeWindowFrom &&
        record?.sendSmsRequest?.deliveryTimeWindowTo &&
        record?.sendSmsRequest?.deliveryTimeWindowDays
      ) {
        return (
          <CustomTag>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div>
                <div>
                  {`${record.sendSmsRequest?.deliveryTimeWindowFrom} ${record.sendSmsRequest?.deliveryTimeWindowTo}`}
                </div>
              </div>

              <Tooltip
                title={
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {record?.sendSmsRequest?.deliveryTimeWindowDays.map(day => (
                      <span key={day}>
                        {translate(`days.${day?.toLowerCase()}`)}
                      </span>
                    ))}
                  </div>
                }
              >
                <Button type="link" color="#5867dd">
                  {translate('deliveryTimeWindowDays')}
                </Button>
              </Tooltip>
            </div>
          </CustomTag>
        );
      }

      return record.sendSmsRequest?.sendAt ? (
        <CustomTag>{formatDateTime(record.sendSmsRequest?.sendAt)}</CustomTag>
      ) : (
        ''
      );
    },
    [translate],
  );

  const columns = useMemo(() => {
    const defaultColumns = [
      {
        title: 'ID',
        dataIndex: 'id',
        render: (_: any, record: RecordType) => record.sendSmsRequest?.id,
      },
      {
        title: translate('table.from'),
        dataIndex: 'from',
        render: (_: any, record: RecordType) => record.sendSmsRequest?.from,
      },
      {
        title: translate('table.message'),
        dataIndex: 'message',
        render: (_: any, record: RecordType) =>
          messageColumn(record.sendSmsRequest?.message || ''),
      },
      {
        title: translate('table.numSms'),
        dataIndex: 'successCount',
        render: (value: string, record: RecordType) =>
          columnListStatus(value, record),
      },
      {
        title: translate('table.status'),
        dataIndex: 'status',
        render: (_: any, record: RecordType) =>
          translate(`status-type.${record.sendSmsRequest?.status}`),
      },
      {
        title: translate('table.type'),
        dataIndex: 'flash',
        render: (_: any, record: RecordType) =>
          record.sendSmsRequest?.flash
            ? translate('table.flash')
            : translate('table.normal'),
      },
      {
        title: translate('table.deliveryTime'),
        dataIndex: 'deliveryTime',
        render: (_: any, record: RecordType) => columnDeliveryTime(record),
      },
      {
        title: translate('table.validityPeriod'),
        dataIndex: 'validityMinutes',
        render: (_: any, record: RecordType) => {
          if (typeof record.sendSmsRequest?.validityMinutes === 'number') {
            if (record.sendSmsRequest?.validityMinutes > 0) {
              return record.sendSmsRequest?.validityMinutes / 60;
            }
            return '0';
          }
          return '';
        },
      },
      {
        title: translate('table.createdBy'),
        dataIndex: 'createdBy',
        render: (_: any, record: RecordType) =>
          record.sendSmsRequest?.createdBy,
      },
      {
        title: translate('table.createdAt'),
        dataIndex: 'createdAt',
        render: (_: any, record: RecordType) =>
          record.sendSmsRequest?.createdAt &&
          dateFormat(record.sendSmsRequest?.createdAt),
      },
      {
        title: translate('columns.actions'),
        dataIndex: 'actions',
        render: (value: any, record: RecordType) => (
          <Button
            type="link"
            color="#5867dd"
            icon={<FileSearchOutlined />}
            onClick={() => {
              setTableToShow('one');
              setSearchParams({
                sendSmsRequestId: record?.sendSmsRequest?.id,
              });
            }}
          />
        ),
      },
    ];
    return defaultColumns.filter(column => {
      if (column.dataIndex === 'sender') {
        return smsAvailableOptions?.sender;
      }
      if (column.dataIndex === 'deliveryTime') {
        return smsAvailableOptions?.deliveryTimeWindow;
      }

      return true;
    });
  }, [
    translate,
    columnListStatus,
    messageColumn,
    columnDeliveryTime,
    setSearchParams,
    smsAvailableOptions,
  ]);

  const handleDateChange = useCallback(
    (values: any) => {
      const start = moment(values.startDate).startOf('day');
      const end = moment(values.endDate).endOf('day');
      setSearchParams({
        createdAtFrom: toISOStringWithOffset(start),
        createdAtTo: toISOStringWithOffset(end),
      });
    },
    [setSearchParams],
  );

  const handleTabsChange = useCallback(
    (key: string) => {
      setTableToShow(key);
      clearSearchParams();
    },
    [clearSearchParams],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        label: translate('table.createdBy'),
        element: (
          <Search
            onSearch={value => setSearchParams({ createdBy: value })}
            onChange={({ target: { value } }) => setCreatedByValue(value)}
            value={createdByValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: 'ID',
        element: (
          <Search
            onSearch={value => setSearchParams({ id: value })}
            onChange={({ target: { value } }) => setIdValue(value)}
            value={idValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: translate('table.message'),
        element: (
          <Search
            onSearch={value => setSearchParams({ message: value })}
            onChange={({ target: { value } }) => setMessageValue(value)}
            value={messageValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: translate('table.createdAt'),
        element: (
          <ConfigProvider locale={getAntLocale(language)}>
            <QuickRangePicker
              onChange={handleDateChange}
              defaultValue={
                createdAtFrom
                  ? [
                      moment(createdAtFrom, 'YYYYMMDD'),
                      moment(createdAtTo, 'YYYYMMDD'),
                    ]
                  : undefined
              }
              style={{ width: '100%' }}
            />
          </ConfigProvider>
        ),
      },
    ],
    [
      createdAtFrom,
      createdAtTo,
      createdByValue,
      handleDateChange,
      idValue,
      language,
      messageValue,
      setSearchParams,
      translate,
    ],
  );

  useEffect(() => {
    setIdValue(id);
  }, [id]);

  return (
    <>
      {tableToShow === 'batch' ? (
        <Table
          title={translate('title.main')}
          columns={columns}
          data={data?.content}
          loading={!data?.content}
          tableKey="sms-send"
          tabs={{
            activeKey: tableToShow,
            list: [
              { label: translate('tabs.batch'), key: 'batch' },
              { label: translate('tabs.one'), key: 'one' },
            ],
            onChange: handleTabsChange,
          }}
          pagination={{ page, linesPerPage, total: data?.totalElements }}
          onTableChange={setSearchParams}
          filters={{
            list: tableFilters,
            openByDefault: true,
            hasValuesInFilters: hasValuesInExtraParams,
            onClearFilters: () => clearSearchParams(),
          }}
          extraActions={
            <>
              <Tooltip placement="top" title={translate('title.newSmsSend')}>
                <Button
                  icon={<PlusCircleOutlined />}
                  size="large"
                  type="link"
                  onClick={() => setVisible(true)}
                />
              </Tooltip>
              <RequestReport
                items={[
                  {
                    role: rolesConstants.REQUEST_REPORT,
                    path: '/service-proxy/sms-request/batch/report',
                    type: 'SMS_REQUESTS',
                    method: 'get',
                    filters: {
                      createdBy,
                      createdAtFrom,
                      createdAtTo,
                      message,
                    },
                  },
                ]}
              />
            </>
          }
        />
      ) : (
        <SmsSendDetails
          setTableToShow={setTableToShow}
          handleTabsChange={handleTabsChange}
          tableToShow={tableToShow}
        />
      )}
      <DeviceActionModal
        visible={visible}
        actionKey={ActionTypes.SEND_SMS}
        onClose={() => {
          setVisible(false);
          mutate();
        }}
        devicesProps={[]}
      />
    </>
  );
};

export default SmsRequest;
